import { Suspense, lazy, useMemo, type ReactElement } from 'react'
import { Route, RouteObject, Routes } from 'react-router-dom'
import '@/App.css'

const NoCustomerFound = lazy(() => import('@/components/NoCustomerFound'))
import PageTitle from '@/components/PageTitle'

const RedirectInaccessiblePages = lazy(
  () => import('@/components/RedirectInaccessiblePages')
)
import ErrorBoundary from '@/lib/misc/ErrorBoundary'
import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { useApp } from '@/lib/provider/AppProvider'
import { useAuth } from '@/lib/provider/AuthProvider'
import { AnimatePresence } from 'framer-motion'
import MemberLayout from '@/layouts/MemberLayout'
import NoLayout from '@/layouts/NoLayout'
import LoadingSpinner from './components/LoadingSpinner'
import ServiceDetailsPageViaServiceId from './pages/ServiceDetails/ServiceDetailsPageViaServiceId'

const ServiceWizardProvider = lazy(
  () => import('@/lib/provider/ServiceWizardProvider')
)

const CreatePassword = lazy(() => import('@/pages/auth/CreatePassword'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'))
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail'))
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'))
const DashboardV2 = lazy(() => import('@/pages/dashboard/DashboardV2'))
const AddUser = lazy(() => import('@/pages/settings/AddUser'))
const CreateCustomer = lazy(() => import('@/pages/settings/CreateCustomer'))
const EditUser = lazy(() => import('@/pages/settings/EditUser'))
const Tickets = lazy(() => import('@/pages/tickets/Tickets'))
const Support = lazy(() => import('@/pages/Support'))
const Logout = lazy(() => import('@/pages/auth/Logout'))
const Login = lazy(() => import('@/pages/auth/Login'))
const SignUp = lazy(() => import('@/pages/auth/SignUp'))
const OIDCLogin = lazy(() =>
  import('@/pages/auth/OIDCLogin').then((module) => ({
    default: module.OIDCLogin,
  }))
)
const AuthOIDCLogin = lazy(() =>
  import('@/pages/auth/OIDCLogin').then((module) => ({
    default: module.AuthOIDCLogin,
  }))
)
const IpTransit = lazy(() => import('@/pages/ip-transit/IpTransit'))
const IpTransitOverGRE = lazy(
  () => import('@/pages/ip-transit/IpTransitOverGRE')
)
const IpTransitV2 = lazy(() => import('@/pages/ip-transit-v2/IpTransitV2'))
const FlexEthernet = lazy(() => import('@/pages/flex-ethernet/FlexEthernet'))
const IpAccess = lazy(() => import('@/pages/ip-access/IpAccess'))
const DDoSProtection = lazy(
  () => import('@/pages/ddos-protection/DDoSProtection')
)
const Privacy = lazy(() => import('@/pages/legal/Privacy'))
const Terms = lazy(() => import('@/pages/legal/Terms'))
const Billing = lazy(() => import('@/pages/settings/Billing'))
const CompanyProfile = lazy(() => import('@/pages/settings/CompanyProfile'))
const Security = lazy(() => import('@/pages/settings/Security'))
const FlexInsights = lazy(() => import('@/pages/settings/FlexInsights'))
const Insights = lazy(() => import('@/pages/insights/Insights'))
const FlowAnalysis = lazy(() => import('@/pages/flow-analysis/FlowAnalysis'))
const UserProfile = lazy(() => import('@/pages/settings/UserProfile'))
const Analytics = lazy(() => import('@/pages/settings/Analytics'))
const Users = lazy(() => import('@/pages/settings/Users'))
const EditFirewall = lazy(() => import('@/pages/firewall/EditFirewall'))
const Firewall = lazy(() => import('@/pages/firewall/Firewall'))

// Tenants components
const Customers = lazy(() => import('@/pages/customers/Customers'))
const Customer = lazy(() => import('@/pages/customers/Customer'))
const NewCustomer = lazy(() => import('@/pages/customers/NewCustomer'))
const DynamicRouteHandler = lazy(() => import('@/pages/DynamicRouteHandler'))

const IpTransitServiceDetails = lazy(
  () => import('@/pages/ip-transit/IpTransitServiceDetails')
)
const IpAccessServiceDetails = lazy(
  () => import('@/pages/ip-access/IpAccessServiceDetails')
)
const FlexEthernetServiceDetails = lazy(
  () => import('@/pages/flex-ethernet/FlexEthernetServiceDetails')
)
const FirewallServiceDetails = lazy(
  () => import('@/pages/firewall/FirewallServiceDetails')
)
const ExtensionServiceDetails = lazy(
  () => import('@/pages/extensions/ExtensionServiceDetails')
)
const ServiceComponentDetails = lazy(
  () => import('@/pages/service-component/ServiceComponentDetails')
)
const DDoSProtectionServiceDetails = lazy(
  () => import('@/pages/ddos-protection/DDoSProtectionServiceDetails')
)
const GenericServiceDetails = lazy(
  () => import('@/pages/ServiceDetails/GenericServiceDetails')
)

const GlobalRoutes: RouteObject[] = [
  { path: 'legal/privacy', element: <Privacy /> },
  { path: 'legal/terms', element: <Terms /> },
  { path: '/sign-up/verify/:confirmationToken', element: <VerifyEmail /> },
]

const UnauthenticatedRoutes: RouteObject[] = [
  { path: '/', element: <Login /> },
  { path: '/login', element: <Login /> },
  { path: '/sign-up', element: <SignUp /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/oidc-login/', element: <OIDCLogin /> },
  { path: '*', element: <DynamicRouteHandler /> },
  { path: '/reset-password/:uid/:token', element: <ResetPassword /> },
  { path: '/create-password/:uid/:token', element: <CreatePassword /> },
]

const AuthenticatedRoutesRest: RouteObject[] = [
  { path: 'settings/create-customer', element: <CreateCustomer /> },
  { path: 'user-profile', element: <UserProfile /> },
  { path: 'settings/analytics', element: <Analytics /> },
  { path: 'tickets', element: <Tickets /> },
  { path: 'tickets/:ticketId', element: <Tickets /> },
  { path: 'support', element: <Support /> },
  { path: 'logout', element: <Logout /> },
  { path: 'customers', element: <Customers /> },
  { path: 'customers/:customer', element: <Customer /> },
  { path: 'customers/new', element: <NewCustomer /> },
  { path: '/oidc-login/', element: <AuthOIDCLogin /> },
  { path: '*', element: <DynamicRouteHandler /> },
]

const CustomerOnlyRoutes: RouteObject[] = [
  { path: 'settings/users', element: <Users /> },
  { path: 'settings/users/:userId/edit', element: <EditUser /> },
  { path: 'settings/users/new', element: <AddUser /> },
  { path: 'billing', element: <Billing /> },
  { path: 'settings/company-profile', element: <CompanyProfile /> },
  { path: 'settings/security', element: <Security /> },
  { path: 'settings/flex-insights', element: <FlexInsights /> },
  { path: 'insights', element: <Insights /> },
  { path: 'insights/flow-analysis', element: <FlowAnalysis /> },
  // Create new service
  { path: 'services/ip-transit', element: <IpTransit /> },
  { path: 'services/flex-tunnel', element: <IpTransitOverGRE /> },
  { path: 'services/flex-ethernet', element: <FlexEthernet /> },
  { path: 'services/ip-access', element: <IpAccess /> },
  { path: 'services/firewall', element: <Firewall /> },
  { path: 'services/ddos-protection', element: <DDoSProtection /> },
  // Service detail pages
  {
    path: 'services/flex-ethernet/:serviceId',
    element: <FlexEthernetServiceDetails />,
  },
  {
    path: 'services/ip-transit/:serviceId',
    element: <IpTransitServiceDetails />,
  },
  {
    path: 'services/ip-access/:serviceId',
    element: <IpAccessServiceDetails />,
  },
  {
    path: 'services/firewall/:firewallId',
    element: <FirewallServiceDetails />,
  },
  { path: 'services/firewall/:firewallId/edit', element: <EditFirewall /> },
  {
    path: 'services/wavelength/:serviceId',
    element: <GenericServiceDetails />,
  },
  {
    path: 'services/extensions/:serviceId',
    element: <ExtensionServiceDetails />,
  },
  {
    path: 'services/port/:serviceComponentId',
    element: <ServiceComponentDetails serviceType="PORT" />,
  },
  {
    path: 'services/lag/:serviceComponentId',
    element: <ServiceComponentDetails serviceType="LAG" />,
  },
  {
    path: 'services/ddos-protection/:serviceId',
    element: <DDoSProtectionServiceDetails />,
  },
  {
    path: 'services/detail/:serviceId',
    element: <GenericServiceDetails />,
  },
  { path: 'services/:serviceId', element: <ServiceDetailsPageViaServiceId /> },
]

function buildFlaggedRoutes(flags: {
  dashboardV2: boolean
  ipTransitV2: boolean
}): {
  authenticatedRoutes: RouteObject[]
  customerOnlyRoutes: RouteObject[]
} {
  const dashboardElement = flags.dashboardV2 ? <DashboardV2 /> : <Dashboard />
  const ipTransitElement = flags.ipTransitV2 ? <IpTransitV2 /> : <IpTransit />

  return {
    authenticatedRoutes: [
      { path: '/', element: dashboardElement },
      ...AuthenticatedRoutesRest,
    ],
    customerOnlyRoutes: CustomerOnlyRoutes.map((route) =>
      route.path === 'services/ip-transit'
        ? { ...route, element: ipTransitElement }
        : route
    ),
  }
}

function App() {
  const [appState] = useApp()
  const [authState] = useAuth()
  const dashboardV2Enabled = useFeatureFlag(FLAGS.DASHBOARD_V2)
  const ipTransitV2Enabled = useFeatureFlag(FLAGS.IP_TRANSIT_V2)
  const isCustomerOptionalEnabled = useFeatureFlag(FLAGS.CUSTOMER_OPTIONAL)

  const { authenticatedRoutes, customerOnlyRoutes } = useMemo(
    () =>
      buildFlaggedRoutes({
        dashboardV2: dashboardV2Enabled,
        ipTransitV2: ipTransitV2Enabled,
      }),
    [dashboardV2Enabled, ipTransitV2Enabled]
  )

  const showCustomerForm = useMemo(() => {
    if (!authState.user) return false
    if (authState.user.customer_id) return false

    return !isCustomerOptionalEnabled
  }, [isCustomerOptionalEnabled, authState.user])

  const CustomerRoutes: RouteObject[] = useMemo(() => {
    if (!authState.user) {
      return [
        ...UnauthenticatedRoutes,
        ...[...authenticatedRoutes, ...customerOnlyRoutes].map((route) => ({
          ...route,
          element: <RedirectInaccessiblePages />,
        })),
      ]
    }

    const customerRoutes = !showCustomerForm
      ? customerOnlyRoutes
      : customerOnlyRoutes.map((route) => ({
          ...route,
          element: <NoCustomerFound />,
        }))

    return [
      ...customerRoutes,
      ...authenticatedRoutes,
      ...UnauthenticatedRoutes.map((route) => ({
        ...route,
        element: <RedirectInaccessiblePages />,
      })),
    ]
  }, [
    authState.user,
    authenticatedRoutes,
    customerOnlyRoutes,
    showCustomerForm,
  ])

  if (appState.waffleFlags.length === 0) {
    return null
  }

  const wrapWithServiceWizardProvider = (element: ReactElement) => (
    <ServiceWizardProvider>{element}</ServiceWizardProvider>
  )

  return (
    <ErrorBoundary>
      <PageTitle />

      <AnimatePresence mode="wait">
        <Routes>
          <Route
            path="/"
            element={
              authState.user ? (
                <MemberLayout key="member-layout" />
              ) : (
                <NoLayout key="no-layout" />
              )
            }
          >
            {GlobalRoutes?.map((el) => (
              <Route
                key={el.path}
                path={el.path}
                element={<Suspense>{el.element}</Suspense>}
              />
            ))}
            {CustomerRoutes?.map((el) => {
              const suspenseFallback = el.path?.startsWith('services/') ? (
                <div className="flex h-screen items-center justify-center">
                  <LoadingSpinner className="size-24 text-brand-1 opacity-50" />
                </div>
              ) : null

              return (
                <Route
                  key={el.path ?? 'customer-route'}
                  path={el.path}
                  element={
                    <Suspense fallback={suspenseFallback}>
                      {authState.user?.customer_id || isCustomerOptionalEnabled
                        ? wrapWithServiceWizardProvider(
                            el.element as ReactElement
                          )
                        : el.element}
                    </Suspense>
                  }
                />
              )
            })}
          </Route>
        </Routes>
      </AnimatePresence>
    </ErrorBoundary>
  )
}

export default App
